import useMutation from '../useMutation';
import useQuery from '../useQuery';
import { inventoryKeyPrefix, salesKeyPrefix } from './useSaleMutations';


/**
 * Per-channel return-window settings (M25 #436/#437) at
 * `/sales/<seller_id>/return-windows/`. GET returns one resolved row per
 * SaleChannel (`{channel, days, is_default}`); any write role reads, but the view
 * is Owner/Manager-write (Viewer 403, Lister no access — the caller hides the
 * editor accordingly). Mirrors `useChannelsQuery` (same wrapper + shape).
 */
function useReturnWindowSettingsQuery(sellerId, params) {
  return useQuery(`/sales/${sellerId}/return-windows/`, {
    enabled: Boolean(sellerId),
    staleTime: 60 * 1000,
    ...params,
  });
}


/**
 * Bulk-upsert per-channel return-window overrides (Owner/Manager). Variables:
 * `{ sellerId, rows, queryKey }` where `rows` is the changed-only `[{channel,
 * days}]` list (see `buildReturnWindowPayload`). PUT is last-wins partial upsert;
 * invalidation runs inside `mutateFn` (the one place `queryClient` is injected),
 * using the caller's held `queryKey` from `useReturnWindowSettingsQuery` PLUS the
 * sales list and cross-resource inventory prefixes (mirroring `useSaleEditMutation`):
 * changed channel days change the server-computed `return_window` on SOLD
 * inventory rows and sales rows, so their cached copies are stale after a save.
 */
function useReturnWindowSettingsMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, rows, queryKey }) => {
      const data = await apiClient.request({
        url: `/sales/${sellerId}/return-windows/`,
        method: 'PUT',
        payload: rows,
      });
      await queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: salesKeyPrefix(sellerId) });
      for (const resource of ['parts', 'listings', 'dashboard']) {
        queryClient.invalidateQueries({ queryKey: inventoryKeyPrefix(sellerId, resource) });
      }
      return data;
    },
    ...params,
  });
}


export { useReturnWindowSettingsMutation, useReturnWindowSettingsQuery };
